import {Injectable} from '@angular/core';
import {Location} from '../../types/location.type';
import {LocationsFormatterService} from './locations-formatter.service';
import unit = Location.unit;

type ILocation = Location.clientSide;
type ILocationWithPosition = ILocation & { position?: number };

@Injectable({
  providedIn: 'root'
})
export class MergeServerClientLocationsService {

  private locations: ILocationWithPosition[];

  constructor(private locationsFormatterService: LocationsFormatterService) {
  }

  public buildLocations(showThermalCorrection: boolean, locationsFromServer: Location.serverSide[], locationsFromClient?: ILocation[], selectedLocationsIDs?: number[]) {
    // we flatten client locations to have two flat structures (server and client locations)
    const locationsFromClientFlat = this.flattenLocationsFromClient(locationsFromClient);

    return this.init()
      .buildLocationsFromServerAndClient(showThermalCorrection, locationsFromServer, locationsFromClientFlat, selectedLocationsIDs)
      .orderLocations()
      .deletePositionProp()
      .returnLocations();
  }

  private init() {
    this.locations = [];

    return this;
  }

  // 'selectedLocationsIDs' is a parameter for older views which only saved the IDs of locations selected, instead of a
  // locations object
  private buildLocationsFromServerAndClient(showThermalCorrection: boolean, project_device_locations: Location.serverSide[], savedLocations?: Location.clientSideFlat[],
                                            selectedLocationsIDs?: number[]) {
    this.locations = project_device_locations.reduce((acc, pdl) => {
      if (pdl.parent) {
        return acc;
      }
      if (!pdl.active) {
        return acc;
      }
      const location = this.buildLocationWithoutChildren(showThermalCorrection, pdl, savedLocations, selectedLocationsIDs);
      location.child = pdl.children.reduce((childAcc, childPdl) => {
        if (!childPdl.active) {
          return childAcc;
        }
        childAcc.push(this.buildLocationWithoutChildren(showThermalCorrection, childPdl, savedLocations, selectedLocationsIDs, location));
        return childAcc;
      }, []);
      acc.push(location);
      return acc;
    }, []);

    return this;
  }

  private buildLocationWithoutChildren(showThermalCorrection: boolean, projectDeviceLocation: Location.serverSide, savedLocations?: Location.clientSideFlat[],
                                       selectedLocationsIDs?: number[], parent?: Location.clientSide): ILocationWithPosition {
    const location: any = {};
    const savedLocation = savedLocations ? savedLocations.find(
      (location) => projectDeviceLocation.id === location.id)
      : null;
    location.id = projectDeviceLocation.id;
    location.name = projectDeviceLocation.name;
    location.type = this.locationsFormatterService.getLocationDeviceType(projectDeviceLocation);
    location.length = projectDeviceLocation.length;
    location.unit = this.locationsFormatterService.setTreeLocationUnit([projectDeviceLocation.unit ? projectDeviceLocation.unit.value : null], location.id, false, location.type, location.child, showThermalCorrection);
    if (savedLocation) {
      location.expanded = savedLocation.expanded;
      location.selected = savedLocation.selected;
      location.unit.forEach((unit: unit) => {
        const savedUnit = savedLocation.unit ? savedLocation.unit.find((u) => u.unit === unit.unit) : null;
        if (savedUnit) {
          unit.selected = savedUnit.selected;
        }
      });
      const parentId = parent ? parent.id : null;
      if ((!parentId && !savedLocation.parentId) || (parentId === savedLocation.parentId)) {  // we keep the position only if the location is still in the same group
        location.position = savedLocation.position;
      } else {
        location.position = -1;
      }
    } else {
      // this is for selecting locations whose ID is passed as URL params + 'older' locations which only saved the IDs of selected locations
      // (instead of a location object)
      const isLocationIDSelected = selectedLocationsIDs ? selectedLocationsIDs.find(
        (id) => id === projectDeviceLocation.id
        )
        : false;
      location.selected = !!isLocationIDSelected;
      location.expanded = location.selected;
      if (location.child) {
        location.child.forEach((child) => {
          child.selected = location.selected;
          child.expanded = location.selected;
        });
      }
      if (location.unit) {
        location.unit.forEach((unit) => {
          unit.selected = location.selected;
          unit.expanded = location.selected;
        });
      }
      if (parent && location.selected) {  // we change this value on parent only if the location was selected
        parent.selected = true;
        parent.expanded = true;
      }
      location.position = -1;
    }

    return location;
  }

  private flattenLocationsFromClient(locations: ILocation[]) {
    if (!locations) {
      return null;
    }

    const copyAllPropsExceptedChildren = (sourceLocation: ILocation, position: number, parentId = null) => {
      // @ts-ignore
      let copy: Location.clientSideFlat = {position, parentId};
      Object.entries(sourceLocation).forEach(
        ([key, value]) => {
          if ('child' !== key) {
            copy[key] = value;
          }
        }
      );
      return copy;
    };

    let flatLocations: Location.clientSideFlat[] = [];
    locations.forEach((location, index) => {
      flatLocations.push(copyAllPropsExceptedChildren(location, index));
      if (location.child) {
        const childrenCopy = location.child.map(
          (childLocation, index,) => copyAllPropsExceptedChildren(childLocation, index, location.id));
        flatLocations = flatLocations.concat(childrenCopy);
      }
    });

    return flatLocations;
  }

  private orderLocations() {
    const compareLocationsPositions = (locationA: ILocationWithPosition, locationB: ILocationWithPosition) => {
      if (-1 === locationA.position && -1 === locationB.position) {
        return 0;
      } else if (-1 === locationA.position) {
        return 1;
      } else if (-1 === locationB.position) {
        return -1;
      } else {
        return locationA.position - locationB.position;
      }
    };
    this.locations.sort(compareLocationsPositions);
    this.locations.forEach(
      (location) => location.child.sort(compareLocationsPositions)
    );

    return this;
  }

  private deletePositionProp() {
    const deleteProp = (location: ILocationWithPosition) => delete location.position;
    this.locations.forEach((location) => {
      deleteProp(location);
      if (location.child) {
        location.child.forEach(deleteProp);
      }
    });

    return this;
  }

  private returnLocations(): ILocation[] {
    //@ts-ignore
    return this.locations;
  }


}
